import type { InterviewInviteListItemDtoEndedBy } from '@sara/api-client';
import { Badge } from '@sara/ui/components/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@sara/ui/components/tooltip';
import { cn } from '@sara/ui/lib/utils';
import { Bot, CircleHelp, Clock, Link2, Phone, User, type LucideIcon } from 'lucide-react';

/**
 * Shared presentational atoms for an invite's call history — the attempt meter
 * (2/3) and the "who ended the call" tag. Reused anywhere we surface call
 * attempts (the sessions table, the session detail page). Requires a
 * <TooltipProvider> ancestor.
 */

// Both list and detail DTOs generate distinct-but-identical EndedBy string
// unions; either is assignable to this one.
export type CallEndedBy = NonNullable<InterviewInviteListItemDtoEndedBy>;

interface EndedByMeta {
  Icon: LucideIcon;
  /** Compact inline label. */
  label: string;
  /** Fuller explanation surfaced on hover. */
  detail: string;
}

// Mirrors the EndedBy enum (apps/api prisma schema): who terminated the surfaced attempt.
const ENDED_BY_META: Record<CallEndedBy, EndedByMeta> = {
  CANDIDATE: {
    Icon: User,
    label: 'Candidate ended',
    detail: 'The candidate ended the call — clicked finish, hung up, or closed the tab.',
  },
  AGENT: {
    Icon: Bot,
    label: 'Agent ended',
    detail: 'The AI agent ended the call itself.',
  },
  SYSTEM: {
    Icon: Clock,
    label: 'Timed out',
    detail: 'Ended automatically after a stretch of silence or inactivity.',
  },
  UNKNOWN: {
    Icon: CircleHelp,
    label: 'Ended',
    detail: 'The call ended for an unrecognized reason.',
  },
};

export function AttemptMeter({
  attempts,
  maxAttempts,
  depleted,
}: {
  attempts: number;
  maxAttempts: number;
  /** Tint the meter to flag that usable attempts ran out. */
  depleted?: boolean;
}) {
  const used = Math.min(attempts, maxAttempts);
  const summary =
    used >= maxAttempts ? 'All attempts used' : `${used} of ${maxAttempts} attempts used`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center gap-1.5" aria-label={summary}>
          <span className="flex items-center gap-[3px]" aria-hidden="true">
            {Array.from({ length: maxAttempts }, (_, i) => (
              <span
                key={i}
                className={cn(
                  'h-1.5 w-2.5 rounded-[2px] transition-colors',
                  i < used ? (depleted ? 'bg-warning' : 'bg-foreground/55') : 'bg-border'
                )}
              />
            ))}
          </span>
          <span className={cn('tabular-nums', depleted && 'font-medium text-warning')}>
            {used}/{maxAttempts}
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{summary}</TooltipContent>
    </Tooltip>
  );
}

/**
 * How this attempt reached the candidate: an outbound phone call (dial) vs a
 * shared browser link. Makes call sessions visually distinct from link sessions.
 */
export function ChannelTag({ viaPhone, className }: { viaPhone: boolean; className?: string }) {
  const { Icon, label, detail } = viaPhone
    ? {
        Icon: Phone,
        label: 'Phone call',
        detail: 'Outbound phone call placed to the candidate via ElevenLabs.',
      }
    : {
        Icon: Link2,
        label: 'Link',
        detail: 'Browser session — the candidate opened a shared interview link.',
      };
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant={viaPhone ? 'info' : 'neutral'} className={cn('gap-1', className)}>
          <Icon className="h-3 w-3 shrink-0" strokeWidth={1.7} aria-hidden="true" />
          {label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>{detail}</TooltipContent>
    </Tooltip>
  );
}

export function EndedByTag({ endedBy, className }: { endedBy: CallEndedBy; className?: string }) {
  const { Icon, label, detail } = ENDED_BY_META[endedBy];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn('inline-flex items-center gap-1', className)}>
          <Icon className="h-3 w-3 shrink-0" strokeWidth={1.7} aria-hidden="true" />
          <span>{label}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{detail}</TooltipContent>
    </Tooltip>
  );
}
